import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
import {
  ArrowRight,
  Check,
  ChevronDown,
  CirclePlay,
  FileDown,
  FolderOpen,
  LayoutGrid,
  Pencil,
  Plus,
  Search,
  Settings2,
  Trash2,
} from 'lucide-react';
import { JobIdentity } from './JobIdentity';
import { hostHas } from '../shared/host-capabilities';
import { hostWords } from '../shared/host-words';
import './job-selector.css';

type JobChoice = { id: string; name: string };
/** One area of the current job's setup, with its value once it is known. */
export type JobSetupTile = { id: string; label: string; icon: ReactNode; value?: string };

/**
 * The job's front door: switch jobs, and reach this job's setup. Navigation
 * only; the application owns saves, job opening and where each tile leads.
 * Settings for the job hang on the job's own name, never on a shared gear, and
 * so do the rare Rename, Download job setup and Delete job.
 */
export function JobSelector({
  jobs,
  currentJobId,
  onOpen,
  onManage,
  onCreate,
  setup,
  manage,
  unfinishedImport,
  onOpenChange,
  loading = false,
  error = '',
}: {
  jobs: readonly JobChoice[];
  currentJobId?: string;
  onOpen: (id: string) => void;
  onManage: () => void;
  onCreate?: () => void;
  setup?: { tiles: readonly JobSetupTile[]; onOpen: (id: string) => void; onOpenAll?: () => void };
  manage?: { onRename: () => void; onDownload: () => void; onDelete: () => void };
  /** An import into this job that was paused or stopped: its way back, here until it is finished. */
  unfinishedImport?: { progress: string; onContinue: () => void };
  onOpenChange?: (open: boolean) => void;
  loading?: boolean;
  error?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const trigger = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const keyboardOpening = useRef(false);
  const id = useId();
  const current = jobs.find((job) => job.id === currentJobId);
  const door = Boolean(setup && current);
  const searchable = jobs.length >= 8;
  const visible = jobs.filter(
    (job) => !searchable || job.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()),
  );
  const close = (restore = false) => {
    setOpen(false);
    setQuery('');
    if (restore) trigger.current?.focus({ preventScroll: true });
  };
  const choose = (jobId: string) => {
    close(true);
    if (jobId !== currentJobId) onOpen(jobId);
  };
  useEffect(() => {
    close();
  }, [currentJobId]);
  useEffect(() => {
    onOpenChange?.(open);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const inside = (target: EventTarget | null) =>
      target instanceof Node &&
      (popup.current?.contains(target) || trigger.current?.contains(target));
    const outside = (event: PointerEvent) => {
      if (!inside(event.target)) close();
    };
    const focus = (event: FocusEvent) => {
      if (!inside(event.target)) close();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close(true);
      }
    };
    document.addEventListener('keydown', escape, true);
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', focus);
    return () => {
      document.removeEventListener('keydown', escape, true);
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('focusin', focus);
    };
  }, [open]);
  useLayoutEffect(() => {
    if (!open) return;
    const position = () => {
      const anchor = trigger.current?.getBoundingClientRect(),
        panel = popup.current;
      if (!anchor || !panel) return;
      panel.style.left = `${Math.max(8, Math.min(anchor.left, innerWidth - panel.offsetWidth - 8))}px`;
      panel.style.top = `${anchor.bottom + 6}px`;
      panel.style.maxHeight = `${Math.max(100, innerHeight - anchor.bottom - 14)}px`;
    };
    position();
    if (keyboardOpening.current)
      (
        popup.current?.querySelector<HTMLElement>('input, [aria-checked="true"]') ??
        popup.current?.querySelector<HTMLElement>('[role="menuitemradio"], .job-selector-manage')
      )?.focus();
    const observer = new ResizeObserver(position);
    if (trigger.current) observer.observe(trigger.current);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [open]);
  const focusChoice = () =>
    popup.current?.querySelector<HTMLButtonElement>('[role="menuitemradio"]')?.focus();
  const switcher = (
    <>
      {searchable && (
        <label className="job-selector-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            aria-label={`Find ${hostWords().job}`}
            placeholder={`Find a ${hostWords().job}`}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
      )}
      {loading ? (
        <p role="status">Loading jobs…</p>
      ) : error ? (
        <p role="alert" data-telemetry="error:job-list-load-failed">
          The job list could not be loaded. Open All {hostWords().jobs} to try again.
        </p>
      ) : (
        <div className="job-selector-list" role="menu" aria-label={hostWords().jobsCapital}>
          {visible.map((job) => (
            <button
              key={job.id}
              type="button"
              role="menuitemradio"
              tabIndex={-1}
              aria-checked={job.id === currentJobId}
              onClick={() => choose(job.id)}
            >
              {door && <JobIdentity key={job.id} jobId={job.id} />}
              <span>{job.name}</span>
              {job.id === currentJobId && <Check size={16} aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
      {!loading && !error && !visible.length && (
        <p role="status">
          {query ? `No matching ${hostWords().jobs}` : `No ${hostWords().jobs} yet`}
        </p>
      )}
      {onCreate && (
        <button
          type="button"
          className="job-selector-create"
          onClick={() => {
            close(true);
            onCreate();
          }}
        >
          <Plus size={16} aria-hidden="true" /> New job
        </button>
      )}
      <button
        type="button"
        className="job-selector-manage"
        onClick={() => {
          close(true);
          onManage();
        }}
      >
        <LayoutGrid size={16} aria-hidden="true" /> All {hostWords().jobs}
      </button>
    </>
  );
  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={`job-selector-trigger${door ? ' job-door-trigger' : ''}`}
        aria-label={`Switch ${hostWords().job}${current ? `: ${current.name}` : ''}`}
        title={current?.name}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onMouseDown={(event) => event.preventDefault()}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && open) {
            event.preventDefault();
            event.stopPropagation();
            close(true);
          }
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            event.stopPropagation();
            keyboardOpening.current = true;
            if (open) focusChoice();
            else setOpen(true);
          }
        }}
        onClick={(event) => {
          if (open) close(true);
          else {
            keyboardOpening.current = event.detail === 0;
            setOpen(true);
          }
        }}
      >
        {door && current ? (
          <JobIdentity key={current.id} jobId={current.id} />
        ) : (
          <FolderOpen className="job-selector-compact-icon" size={16} aria-hidden="true" />
        )}
        <span>{current?.name ?? `Choose ${hostWords().job}`}</span>
        <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open &&
        createPortal(
          <div
            ref={popup}
            id={id}
            className={`job-selector-popup${door ? ' job-door' : ''}`}
            role="dialog"
            aria-label={`Switch ${hostWords().job}`}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === 'Escape') {
                event.preventDefault();
                close(true);
                return;
              }
              if (event.key === 'F2' && door && manage) {
                event.preventDefault();
                close(true);
                manage.onRename();
                return;
              }
              const items = [
                ...event.currentTarget.querySelectorAll<HTMLButtonElement>(
                  '[role="menuitemradio"]',
                ),
              ];
              const index = items.indexOf(document.activeElement as HTMLButtonElement);
              if (
                ['ArrowDown', 'ArrowUp'].includes(event.key) &&
                items.length &&
                (index >= 0 || event.target instanceof HTMLInputElement)
              ) {
                event.preventDefault();
                items[
                  (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length
                ]?.focus();
              } else if (
                !(event.target instanceof HTMLInputElement) &&
                ['Home', 'End'].includes(event.key) &&
                items.length &&
                index >= 0
              ) {
                event.preventDefault();
                items[event.key === 'Home' ? 0 : items.length - 1]?.focus();
              } else if (
                event.key === 'Enter' &&
                event.target instanceof HTMLInputElement &&
                visible[0]
              ) {
                event.preventDefault();
                choose(visible[0].id);
              }
            }}
            onKeyUp={(event) => event.stopPropagation()}
          >
            {door && current && setup ? (
              <>
                <header className="job-door-heading">
                  <JobIdentity key={current.id} jobId={current.id} />
                  <span>
                    <strong>{current.name}</strong>
                    <small>{hostWords().jobCapital}</small>
                  </span>
                  {manage && (
                    <button
                      type="button"
                      className="job-door-rename"
                      aria-label={`Rename ${current.name}`}
                      title="Rename · F2"
                      aria-keyshortcuts="F2"
                      onClick={() => {
                        close(true);
                        manage.onRename();
                      }}
                    >
                      <Pencil size={15} aria-hidden="true" />
                    </button>
                  )}
                  {setup.onOpenAll && (
                    <button
                      type="button"
                      className="job-door-open-setup"
                      aria-label="Open job setup"
                      title="Job setup"
                      onClick={() => {
                        close();
                        setup.onOpenAll?.();
                      }}
                    >
                      <Settings2 size={18} aria-hidden="true" />
                    </button>
                  )}
                </header>
                <div className="job-door-body">
                  <section className="job-door-switch" aria-label="Switch job">
                    <h3>Switch job</h3>
                    {switcher}
                  </section>
                  <section className="job-door-setup" aria-label="This job's setup">
                    <h3>This job's setup</h3>
                    <div className="job-door-tiles">
                      {unfinishedImport && (
                        <button
                          type="button"
                          className="job-door-tile job-door-action"
                          aria-label={`Finish import: ${unfinishedImport.progress}`}
                          onClick={() => {
                            close(true);
                            unfinishedImport.onContinue();
                          }}
                        >
                          <span className="job-door-tile-icon" aria-hidden="true">
                            <CirclePlay size={18} />
                          </span>
                          <strong>Finish import</strong>
                          <small>{unfinishedImport.progress}</small>
                          <ArrowRight className="job-door-tile-go" size={15} aria-hidden="true" />
                        </button>
                      )}
                      {setup.tiles.map((tile) => (
                        <button
                          key={tile.id}
                          type="button"
                          className="job-door-tile"
                          aria-label={`${tile.label}${tile.value ? `: ${tile.value}` : ''}`}
                          onClick={() => {
                            close();
                            setup.onOpen(tile.id);
                          }}
                        >
                          <span className="job-door-tile-icon" aria-hidden="true">
                            {tile.icon}
                          </span>
                          <strong>{tile.label}</strong>
                          <small>{tile.value ?? ' '}</small>
                          <ArrowRight className="job-door-tile-go" size={15} aria-hidden="true" />
                        </button>
                      ))}
                      {/* The job's own rare actions are part of its setup list, not a separate bar. */}
                      {manage && (
                        <>
                          {hostHas('job-setup-files') && (
                            <button
                              type="button"
                              className="job-door-tile job-door-action"
                              aria-label="Download job setup"
                              onClick={() => {
                                close(true);
                                manage.onDownload();
                              }}
                            >
                              <span className="job-door-tile-icon" aria-hidden="true">
                                <FileDown size={18} />
                              </span>
                              <strong>Download job setup</strong>
                              <small>For another computer</small>
                              <ArrowRight
                                className="job-door-tile-go"
                                size={15}
                                aria-hidden="true"
                              />
                            </button>
                          )}
                          {/* PAIGE: only where the host deletes a job from here (workbench-port.md). */}
                          {hostHas('job-delete') && (
                            <button
                              type="button"
                              className="job-door-tile job-door-action job-door-delete"
                              aria-label="Delete job…"
                              onClick={() => {
                                close(true);
                                manage.onDelete();
                              }}
                            >
                              <span className="job-door-tile-icon" aria-hidden="true">
                                <Trash2 size={18} />
                              </span>
                              <strong>Delete job…</strong>
                              <small>Asks you to confirm</small>
                              <ArrowRight
                                className="job-door-tile-go"
                                size={15}
                                aria-hidden="true"
                              />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </section>
                </div>
              </>
            ) : (
              switcher
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
